import { lazy } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'
import type { ManifestRouteDefinition } from '@/core/manifest/types'
import ProtectedRoute from '@/shared/components/ProtectedRoute'
import { PermissionGate } from '@/core/routing/PermissionGate'
import { allManifestRouteRoots } from '@/core/manifest/app.manifest'
import { ROUTES } from '@/app/routes.constants'

const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage/LoginPage').then((m) => ({
    default: m.LoginPage,
  }))
)
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/ForgotPassword/ForgotPassword').then((m) => ({
    default: m.default,
  }))
)
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/pages/ResetPassword/ResetPassword').then((m) => ({
    default: m.default,
  }))
)
const OTPPage = lazy(() =>
  import('@/features/auth/pages/OTP/OTP').then((m) => ({
    default: m.default,
  }))
)
const NotFoundPage = lazy(() =>
  import('@/features/common/pages/NotFoundPage/NotFoundPage').then((m) => ({
    default: m.NotFoundPage,
  }))
)

function wrap(def: ManifestRouteDefinition, node: React.ReactNode) {
  if (def.isPublic) {
    return <>{node}</>
  }
  return (
    <ProtectedRoute>
      <PermissionGate permissions={def.permissions}>{node}</PermissionGate>
    </ProtectedRoute>
  )
}

function manifestToRoute(def: ManifestRouteDefinition, parentIsPublic?: boolean): RouteObject {
  const Lazy = lazy(def.lazyComponent)
  const isPublic = def.isPublic || parentIsPublic
  const routeDefWithInheritedPublic = { ...def, isPublic }
  const element = wrap(routeDefWithInheritedPublic, <Lazy />)

  if (def.isIndex) {
    return { index: true, element }
  }

  const path = def.path
  if (!def.children?.length) {
    if (!path) {
      throw new Error(`Manifest route "${def.key}" needs path`)
    }
    return {
      path: path.startsWith('/') ? path.slice(1) : path,
      element,
    }
  }

  if (!path) {
    throw new Error(`Manifest route "${def.key}" needs path when children exist`)
  }

  const children: RouteObject[] = []
  if (def.indexRedirect) {
    children.push({
      index: true,
      element: <Navigate to={def.indexRedirect} replace />,
    })
  }
  for (const child of def.children) {
    children.push(manifestToRoute(child, isPublic))
  }

  return {
    path: path.startsWith('/') ? path.slice(1) : path,
    element,
    children,
  }
}

export function createManifestRouteObjects(): RouteObject[] {
  const publicRoutes: RouteObject[] = [
    { path: ROUTES.LOGIN, element: <LoginPage /> },
    { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
    { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
    { path: ROUTES.OTP, element: <OTPPage /> },
  ]

  const legacyRedirects: RouteObject[] = [
    {
      path: 'user/create',
      element: (
        <ProtectedRoute>
          <Navigate to="/users/create" replace />
        </ProtectedRoute>
      ),
    },
    {
      path: 'user/map-role',
      element: (
        <ProtectedRoute>
          <Navigate to="/users/active" replace />
        </ProtectedRoute>
      ),
    },
  ]

  const appRoutes = allManifestRouteRoots.map((root) => manifestToRoute(root))

  return [...publicRoutes, ...legacyRedirects, ...appRoutes, { path: '*', element: <NotFoundPage /> }]
}
